import * as React from 'react';
import Box from '@mui/material/Box';
import styled from '@mui/material/styles/styled';
import { Badge, BadgeProps, FormHelperText, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/rootReducer';
import { updateCoach } from '../../../redux/coach/coachSlice';
import { sanitizeInputName } from '../../../utils/userInputUtils';
import VisuallyHiddenInput from '../../Common/VisuallyHiddenInput';
import LabeledInput from '../../Common/LabeledInput';
import CustomPhoneNumberInput from '../../CustomPhoneNumberInput/CustomPhoneNumberInput';
import StyledButton from '../../Common/StyledButton';

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: 6,
    top: 18,
    padding: '13px 4px',
    borderRadius: '50%',
  },
}));

const isPhoneNumberValid = (value: string | undefined | null) => {
  if (value) {
    return isValidPhoneNumber(value);
  }
  return true;
};

export default function UpdatePersonalInfo() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { coach, updateCoachLoading } = useAppSelector((state: RootState) => state.coach);
  const initialSrc = coach.isExternalImage
    ? coach.profilePic
    : `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}${
        coach.profilePic?.startsWith('/') ? coach.profilePic : `/${coach.profilePic}`
      }`;
  const [avatarSrc, setAvatarSrc] = React.useState(initialSrc);
  const [avatar, setAvatar] = React.useState<File | null>(null);

  const minDate = new Date();

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t('therapistOnboarding.validations.firstName')),
    lastName: Yup.string().required(t('therapistOnboarding.validations.lastName')),
    email: Yup.string()
      .email(t('therapistOnboarding.validations.emailInvalid'))
      .required(t('therapistOnboarding.validations.emailRequired')),
    tp: Yup.string()
      .optional()
      .nullable()
      .test(
        'phoneNumber',
        t('therapistOnboarding.validations.phoneNumberInvalid'),
        isPhoneNumberValid
      ),
  });

  const formik = useFormik({
    initialValues: coach,
    validationSchema,
    onSubmit: (values) => {
      const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
      dispatch(updateCoach({ ...values, timeZone, id: coach.id, profilePicFile: avatar }));
    },
  });

  const handleNameChange = (field: string, value: string) => {
    formik.handleChange(field)(sanitizeInputName(value));
  };

  const handlePhoneNumberChange = (pNumber: string) => {
    if (pNumber) {
      formik.setFieldValue('tp', pNumber);
      formik.handleChange('tp')(pNumber);
    } else {
      formik.setFieldValue('tp', '');
    }
  };

  minDate.setFullYear(minDate.getFullYear() - 100);

  const handleOnChangeAvatar = () => {
    const input = document.querySelector('input');
    if (input !== null && input.files !== null) {
      const fileDetails = {
        contentType: input.files[0].type,
        fileName: input.files[0].name,
      };
      const file = input.files[0];
      const src = URL.createObjectURL(file);
      formik.setFieldValue('profilePic', fileDetails);
      setAvatarSrc(src);
      setAvatar(file);
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex' }}>
      <Box padding="36px 32px" display="flex">
        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid display="flex" flexDirection="column">
            {/* first row */}
            <Box>
              <StyledBadge
                badgeContent={
                  <Box component="label">
                    <EditIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                    <VisuallyHiddenInput
                      max={1}
                      id="avatar"
                      accept="image/png, image/jpeg"
                      onChange={handleOnChangeAvatar}
                      type="file"
                    />
                  </Box>
                }
                color="primary"
              >
                <Box
                  height="100px"
                  width="100px"
                  sx={{ borderRadius: '50%' }}
                  component="img"
                  mb="24px"
                  src={avatarSrc}
                />
              </StyledBadge>
              <FormHelperText sx={{ color: 'red', marginTop: '-2px' }}>
                {formik.errors?.profilePic}
              </FormHelperText>
            </Box>
            {/* second row */}
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <LabeledInput
                    label={t('therapistOnboarding.firstName')}
                    id="first-name"
                    type="text"
                    name="firstName"
                    value={formik.values.firstName}
                    handleChange={(event) => handleNameChange('firstName', event.target.value)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.firstName ? formik.errors.firstName : undefined}
                    placeholder={t('therapistOnboarding.firstName')}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LabeledInput
                    label={t('therapistOnboarding.lastName')}
                    id="last-name"
                    type="text"
                    name="lastName"
                    value={formik.values.lastName}
                    handleChange={(event) => handleNameChange('lastName', event.target.value)}
                    onBlur={formik.handleBlur}
                    error={formik.touched.lastName ? formik.errors.lastName : undefined}
                    placeholder={t('therapistOnboarding.lastName')}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LabeledInput
                    label={t('therapistOnboarding.email')}
                    id="email"
                    type="email"
                    value={formik.values.email}
                    handleChange={(e) => formik.handleChange('email')(e.target.value.toLowerCase())}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email ? formik.errors.email : undefined}
                    placeholder={t('therapistOnboarding.email')}
                    required
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomPhoneNumberInput
                    name="tp"
                    value={formik.values.tp}
                    handleChange={handlePhoneNumberChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tp ? formik.errors.tp : undefined}
                    optional
                  />
                </Grid>
              </Grid>
              <Box />
            </Box>

            {/* third row */}
            <Box pt={2} display="flex" flexDirection="row-reverse">
              <StyledButton loading={updateCoachLoading} loadingText={t('common.saving')}>
                {t('common.save')}
              </StyledButton>
            </Box>
          </Grid>
        </form>
      </Box>
    </Box>
  );
}
